/**
 * WebSearch tool processor for the Now Assist Skill Plugin.
 * Handles processing of WebSearch tools defined in NowAssistSkillConfig.
 */

import {
    TOOL_TYPES,
    RESOURCE_ATTRIBUTE_VALUE_SCRIPT,
    RESOURCE_MAPPING_DEFAULTS,
    WEBSEARCH_AI_ANSWERS_CONFIG,
    WEBSEARCH_SCRAPING_CONFIG,
    AI_SEARCH_PROVIDERS,
} from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    getResourceCapability,
    getApplicabilityProperties,
} from '../tool-utils'

/**
 * Process a WebSearch tool and create the necessary records
 *
 * WebSearch tools create:
 * 1. sys_one_extend_resource_mapping - The main tool record
 * 2. sys_one_extend_resource_attribute_mapping - Input attribute mappings (config fields as snake_case)
 * 3. sys_one_extend_resource_attribute_mapping - 5 fixed output attribute mappings
 * 4. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 *
 * Two variants:
 * - 'ai_answers': query, search_type, ai_search_providers, sites, max_tokens, conversation_history
 * - 'searching_and_scraping': query, search_type, sites, num_results, country, max_tokens, conversation_history
 */
export async function processWebSearchTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    if (tool.type !== TOOL_TYPES.WEB_SEARCH) {
        diagnostics.error(arg, `Expected web-search tool but got ${tool.type}`)
        return records
    }

    if (!tool.name) {
        diagnostics.error(arg, 'WebSearch tool requires a name')
        return records
    }

    if (!tool.searchType) {
        diagnostics.error(
            arg,
            `WebSearch tool '${tool.name}' requires a searchType ('ai_answers' or 'searching_and_scraping')`
        )
        return records
    }

    if (!tool.query && !tool.queryScriptValue) {
        diagnostics.error(arg, `WebSearch tool '${tool.name}' requires a query`)
        return records
    }

    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            resource_name: $.val(tool.name),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(getResourceCapability(tool.type)),
            ...getApplicabilityProperties(tool.condition, $),
            depends_on_list: $.val(dependsOnList),
        })),
    })
    records.push(resourceMappingRecord)

    toolRecordsMap.set(tool.name, resourceMappingRecord)

    const inputMappingConfig =
        tool.searchType === 'searching_and_scraping'
            ? WEBSEARCH_SCRAPING_CONFIG.INPUT_MAPPINGS
            : WEBSEARCH_AI_ANSWERS_CONFIG.INPUT_MAPPINGS

    const toolValues: Record<string, string | number | undefined> = {
        query: tool.query,
        searchType: tool.searchType,
        aiSearchProviders: tool.aiSearchProviders,
        sites: tool.sites,
        numResults: tool.numResults,
        country: tool.country,
        maxTokens: tool.maxTokens,
    }

    const scriptedFields: Record<string, { scriptValue: string; valueType: 'script' }> = {}
    if (tool.queryValueType === 'script' && tool.queryScriptValue) {
        scriptedFields['query'] = { scriptValue: tool.queryScriptValue, valueType: 'script' }
    }
    if (tool.sitesValueType === 'script' && tool.sitesScriptValue) {
        scriptedFields['sites'] = { scriptValue: tool.sitesScriptValue, valueType: 'script' }
    }

    for (const attrConfig of inputMappingConfig) {
        const scripted = scriptedFields[attrConfig.fluentName]
        let attributeValueTemplate = ''
        let attributeValueScript = RESOURCE_ATTRIBUTE_VALUE_SCRIPT
        let attributeValueType = 'template'

        if (scripted) {
            attributeValueScript = scripted.scriptValue
            attributeValueType = 'script'
        } else {
            const rawValue = toolValues[attrConfig.fluentName]

            if (rawValue !== undefined && rawValue !== '') {
                attributeValueTemplate = String(rawValue)
            } else if ('default' in attrConfig && attrConfig.default !== undefined) {
                attributeValueTemplate = String(attrConfig.default)
            }

            if (!attrConfig.required && !attributeValueTemplate) {
                continue
            }

            if (attrConfig.fluentName === 'aiSearchProviders' && attributeValueTemplate) {
                const providerKey = attributeValueTemplate as keyof typeof AI_SEARCH_PROVIDERS
                const providerConfig = AI_SEARCH_PROVIDERS[providerKey]
                if (providerConfig) {
                    attributeValueTemplate = JSON.stringify({ ...providerConfig, elm: {} })
                }
            }
        }

        const inputAttributeRecord = await factory.createRecord({
            source: tool._source!,
            table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                attribute_value_script: $.val(attributeValueScript).toCdata(),
                attribute_value_template: $.val(attributeValueTemplate),
                attribute_value_type: $.val(attributeValueType),
                resource_attribute_name: $.val(attrConfig.attributeId),
                resource_mapping: $.val(resourceMappingRecord.getId()),
                type: $.val('input'),
            })),
        })
        records.push(inputAttributeRecord)
    }

    const outputMappingConfig = WEBSEARCH_AI_ANSWERS_CONFIG.OUTPUT_MAPPINGS
    const outputNameToTruncateKey: Record<string, string> = {
        provider: 'provider',
        response: 'response',
        error: 'error',
        errorcode: 'errorcode',
        status: 'status',
    }
    for (const outputAttr of outputMappingConfig) {
        const truncateKey = outputNameToTruncateKey[outputAttr.name] || outputAttr.name
        const truncateValue = tool.truncateConfig?.[truncateKey] ?? false
        const outputAttributeRecord = await factory.createRecord({
            source: tool._source!,
            table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
                attribute_value_script: $.val(RESOURCE_ATTRIBUTE_VALUE_SCRIPT).toCdata(),
                resource_attribute_name: $.val(outputAttr.attributeId),
                resource_mapping: $.val(resourceMappingRecord.getId()),
                truncate: $.val(truncateValue),
                type: $.val('output'),
            })),
        })
        records.push(outputAttributeRecord)
    }

    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
